import React, { FunctionComponent, useContext } from 'react'

import { noop } from 'lodash-es'
import styled from 'styled-components/native'

import { shouldShowCareNavigatorLiveChat } from '@lyrahealth-inc/shared-app-logic/src/features/careNavigator/utils'

import { AppContext } from '../../context'
import { CareNavigatorCarelonDirectoryOptionCard } from '../../molecules/careNavigatorOptionCard/CareNavigatorCarelonDirectoryOptionCard'
import { CareNavigatorEmailOptionCard } from '../../molecules/careNavigatorOptionCard/CareNavigatorEmailOptionCard'
import { CareNavigatorLiveChatOptionCard } from '../../molecules/careNavigatorOptionCard/CareNavigatorLiveChatOptionCard'
import { CareNavigatorPhoneCallOptionCard } from '../../molecules/careNavigatorOptionCard/CareNavigatorPhoneCallOptionCard'
import { CareNavigatorScheduleCallOptionCard } from '../../molecules/careNavigatorOptionCard/CareNavigatorScheduleCallOptionCard'
import { CarelonDirectoryProps } from '../../pages/contactCareNavigatorConsolidated/ContactCareNavigatorConsolidatedPage'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'

export type CareNavigatorConsolidatedOptionsListProps = {
  customerPhoneNumber?: string
  isInternational: boolean
  isLyrians?: boolean
  isHomebaseFlow?: boolean
  isSidePanel?: boolean
  onPressEmail: () => void
  onPressFAQ?: () => void
  onPressLiveChat: () => void
  onPressPhoneCall: () => void
  onPressScheduleCall: () => void
  showLiveChat: boolean
  showScheduleCall?: boolean
  hideCustomerPhoneNumber: boolean
  careNavigatorHeadshot: string
  careNavigatorLicense: string
  careNavigatorName: string
  showMinimalCTAText: boolean
} & CarelonDirectoryProps

const OptionContainer = styled(Flex1View)(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  marginBottom: theme.spacing['32px'],
}))

const OptionChoiceContainer = styled(Flex1View)<{
  isDedicatedCareNavigatorEnabled?: boolean
  theme?: ThemeType
}>(({ isDedicatedCareNavigatorEnabled, theme }) => ({
  marginTop: isDedicatedCareNavigatorEnabled ? undefined : theme.spacing['12px'],
}))

export const CareNavigatorConsolidatedOptionsList: FunctionComponent<CareNavigatorConsolidatedOptionsListProps> = ({
  customerPhoneNumber,
  isInternational,
  isLyrians = false,
  isHomebaseFlow = false,
  isSidePanel = false,
  onPressEmail = noop,
  onPressFAQ = noop,
  onPressLiveChat = noop,
  onPressPhoneCall = noop,
  onPressScheduleCall = noop,
  showLiveChat,
  showScheduleCall = true,
  hideCustomerPhoneNumber,
  careNavigatorHeadshot,
  careNavigatorLicense,
  careNavigatorName,
  showMinimalCTAText = false,
  showCarelonDirectory = false,
  onPressCarelonDirectory,
  isRegionalNetworkCopyEnabled = false,
}) => {
  //  As of PROSPECT-3119 and PROSPECT-3144, the preferred order for the Care Navigator Options are as follows:
  //  International: Phone Call, Live Chat, Email
  //  US: Email, Schedule Call, Phone Call
  //  Lyrians: Email, FAQ
  //  US & Sidepanel: Email, Live Chat, Phone Call, Schedule Call
  //  As of CN 3.0, Live Chat component should be included to global navigation, search for care and homebase entrypoints
  // Lyrians 2026: Email, Phone Call

  const { isDedicatedCareNavigatorEnabled } = useContext(AppContext)

  //PROVREC-4307 Hide "schedule a call" feature on all CN entrypoints except Homebase and Welcome Email.
  const tempHideCareNavigatorScheduleCallFn = true
  // If isDedicatedCareNavigatorEnabled is on and if user is not the homebase page, show new CN section
  const shouldShowNewOrOldCareNavigatorSection = isDedicatedCareNavigatorEnabled
    ? Boolean(careNavigatorHeadshot) && Boolean(careNavigatorName) && !isHomebaseFlow && showScheduleCall
    : showScheduleCall
  const shouldShowOutsideUSLiveChat = shouldShowCareNavigatorLiveChat({
    isLiveChatAvailable: showLiveChat,
    isLyrian: isLyrians,
  })
  const shouldShowUSLiveChat = shouldShowCareNavigatorLiveChat({
    isLiveChatAvailable: Boolean(isSidePanel || isDedicatedCareNavigatorEnabled),
    isLyrian: isLyrians,
  })
  const OutsideUSBasedNavigatorOptions = [
    {
      name: 'PhoneCallOptionCard',
      component: <CareNavigatorPhoneCallOptionCard onPress={onPressPhoneCall} isInternational />,
    },
    shouldShowOutsideUSLiveChat
      ? {
          name: 'LiveChatOptionCard',
          component: <CareNavigatorLiveChatOptionCard onPress={onPressLiveChat} isInternational />,
        }
      : null,
    { name: 'EmailOptionCard', component: <CareNavigatorEmailOptionCard onPress={onPressEmail} isInternational /> },
  ]

  const USBasedNavigatorOptions = [
    {
      name: 'PhoneCallOptionCard',
      component: (
        <CareNavigatorPhoneCallOptionCard
          onPress={onPressPhoneCall}
          customerPhoneNumber={customerPhoneNumber}
          hideCustomerPhoneNumber={hideCustomerPhoneNumber}
        />
      ),
    },
    shouldShowUSLiveChat
      ? {
          name: 'LiveChatOptionCard',
          component: <CareNavigatorLiveChatOptionCard onPress={onPressLiveChat} />,
        }
      : null,
    {
      name: 'EmailOptionCard',
      component: <CareNavigatorEmailOptionCard onPress={onPressEmail} />,
    },
    !tempHideCareNavigatorScheduleCallFn && shouldShowNewOrOldCareNavigatorSection
      ? {
          name: 'ScheduleCallOptionCard',
          component: (
            <CareNavigatorScheduleCallOptionCard
              onPress={onPressScheduleCall}
              careNavigatorHeadshot={careNavigatorHeadshot}
              careNavigatorLicense={careNavigatorLicense}
              careNavigatorName={careNavigatorName}
              showMinimalCTAText={showMinimalCTAText}
            />
          ),
        }
      : null,
    showCarelonDirectory
      ? {
          name: 'CarelonDirectoryOptionCard',
          component: (
            <CareNavigatorCarelonDirectoryOptionCard
              onPress={onPressCarelonDirectory}
              isRegionalNetworkCopyEnabled={isRegionalNetworkCopyEnabled}
            />
          ),
        }
      : null,
  ]

  const NavigatorOptions = () => {
    if (isInternational) {
      return OutsideUSBasedNavigatorOptions
    } else {
      return USBasedNavigatorOptions
    }
  }
  const NavigatorOptionsRendered = NavigatorOptions().map(
    (optionCard) =>
      optionCard && (
        <OptionChoiceContainer isDedicatedCareNavigatorEnabled={isDedicatedCareNavigatorEnabled} key={optionCard.name}>
          {optionCard.component}
        </OptionChoiceContainer>
      ),
  )

  return <OptionContainer>{NavigatorOptionsRendered}</OptionContainer>
}
